// ============================================================
// ParkControlModal.js — Modale principale d'action sur un parc
// Props :
//   show             — booléen contrôlant l'affichage
//   onHide           — ferme la modale
//   client           — objet parc concerné
//   experts          — tableau { id, name, email } depuis l'API
//   onSetIsControled — callback(date, userControlId, exitStandby) pour valider le contrôle
//                      (parc en standby : une question oui/non décide de exitStandby)
//   onSetStandby     — callback pour basculer l'état standby
//   onOpenDelete     — callback(client) pour ouvrir la modale de suppression
//   onOpenEdit       — callback(client) pour ouvrir la modale de modification
// ============================================================

import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col, Collapse } from 'react-bootstrap';
import moment from 'moment';

const ParkControlModal = ({ show, onHide, client, experts, onSetIsControled, onSetStandby, onOpenDelete, onOpenEdit, role, userEmail }) => {
  const [ControlDate,   setControlDate]   = useState('');
  const [collapseMenu,  setCollapseMenu]  = useState(true);
  const [userControlId, setUserControlId] = useState('');
  const [todayChecked,  setTodayChecked]  = useState(false);
  const [askStandby,    setAskStandby]    = useState(false);

  // Pour les utilisateurs simples : expert lié à leur compte (par email)
  const myExpert = (role === 'user' && userEmail)
    ? (experts || []).find(e => e.email === userEmail) || null
    : null;

  const defaultExpertId = () => {
    if (myExpert) return myExpert.id;
    if (client && client.userControlId) return client.userControlId;
    if (experts && experts.length > 0) return experts[0].id;
    return '';
  };

  const handleClose = () => {
    setCollapseMenu(true);
    setTodayChecked(false);
    setControlDate('');
    setUserControlId('');
    onHide();
  };

  const handleTodayCheck = (e) => {
    const checked = e.target.checked;
    setTodayChecked(checked);
    setControlDate(checked ? moment().format('YYYY-MM-DD') : '');
  };

  const submitControl = (exitStandby) => {
    setAskStandby(false);
    const expertId = myExpert
      ? myExpert.id
      : (userControlId !== '' ? parseInt(userControlId, 10) : defaultExpertId());
    onSetIsControled(ControlDate, expertId, exitStandby);
  };

  // Parc en standby avec une date saisie : demander s'il faut le réactiver
  const handleValidate = () => {
    if (client && client.id !== undefined) {
      if (client.standby === 1 && ControlDate) setAskStandby(true);
      else submitControl(false);
    }
  };

  return (
    <>
    <Modal show={askStandby} onHide={() => setAskStandby(false)}>
      <Modal.Header closeButton>
        <Modal.Title>{client && client.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Ce parc est en standby. Voulez-vous le sortir du mode standby ?
        <br/>
        <Button style={{ margin: "5px" }} variant="secondary" onClick={() => submitControl(false)}>Non, rester en standby</Button>
        <Button style={{ margin: "5px" }} className="btn-modal-purple" onClick={() => submitControl(true)}>Oui, réactiver le parc</Button>
      </Modal.Body>
    </Modal>

    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{client && client.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group>
            <Form.Label className="d-flex justify-content-center">Date de controle</Form.Label>
            <Form.Control
              required
              type="date"
              className="d-flex justify-content-center dateControl"
              value={ControlDate}
              onChange={(e) => { setControlDate(e.target.value); setTodayChecked(false); }}
            />

            <div className="d-flex justify-content-center" style={{ marginTop: '6px' }}>
              <Form.Check
                type="checkbox"
                label="Contrôlé aujourd'hui"
                checked={todayChecked}
                onChange={handleTodayCheck}
              />
            </div>

            {myExpert ? (
              <div className="dateControl" style={{ padding: '8px 12px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', color: '#495057', fontSize: '0.9rem', textAlign: 'center' }}>
                Contrôlé par : <strong>{myExpert.name}</strong>
              </div>
            ) : role === 'user' ? (
              <div className="dateControl" style={{ padding: '8px 12px', border: '1px solid #ffc107', borderRadius: '4px', backgroundColor: '#fff8e1', color: '#856404', fontSize: '0.9rem', textAlign: 'center' }}>
                Aucun expert lié à votre compte
              </div>
            ) : (
              <Form.Control
                as="select"
                className="rounded-0 shadow dateControl"
                value={userControlId !== '' ? userControlId : defaultExpertId()}
                onChange={(e) => setUserControlId(e.target.value)}
              >
                {(experts || []).map(function(expert) {
                  return <option key={expert.id} value={expert.id}>{expert.name}</option>;
                })}
              </Form.Control>
            )}
          </Form.Group>

          <Row className="justify-content-md-center">
            <Col className="d-flex justify-content-center" xs={12} lg={12}>
              <Button style={{ margin: "5px" }} className="btn-modal-gray" onClick={(e) => { e.preventDefault(); setCollapseMenu(!collapseMenu); }} aria-controls="collapse-important-buttons">+ d'options</Button>
            </Col>

            <Collapse in={!collapseMenu}>
              <div id="collapse-important-buttons">
                <Col className="d-flex justify-content-center" xs={12} lg={12}>
                  <Button style={{ margin: "5px" }} className="btn-modal-red" onClick={() => onOpenDelete(client)}>Supprimer</Button>
                </Col>
                <Col className="d-flex justify-content-center" xs={12} lg={12}>
                  <Button style={{ margin: "5px" }} className="btn-modal-red" onClick={() => onOpenEdit(client)}>Modifier</Button>
                </Col>
                <Col className="d-flex justify-content-center" xs={12} lg={12}>
                  <Button style={{ margin: "5px" }} className="btn-modal-purple" onClick={onSetStandby}>{client && client.standby === 1 ? 'Parc à nouveau actif' : 'Mettre en standby'}</Button>
                </Col>
              </div>
            </Collapse>

            <Col className="d-flex justify-content-center" xs={12} lg={12}>
              <Button style={{ margin: "5px" }} onClick={handleValidate}>Valider</Button>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
    </Modal>
    </>
  );
};

export default ParkControlModal;
